import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import { sortBy } from 'ramda';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';

import { types } from './actions';
import {
  materialClassSchema,
  materialFamilySchema,
  materialSchema,
  supplierMaterialSchema,
} from './entities';

const initialState = {
  entities: {},
  classIds: null,
  excludedMaterialsById: {},
  familyIds: null,
  hasFetched: false,
  isFetching: false,
  materialIds: null,
  materialSelectorSettings: null,
  supplierMaterialIds: null,
  storedMaterialOptionsForCascader: [],
};

const entitiesReducer = createEntitiesReducer(
  ['materialClasses', 'materialFamilies', 'materials', 'supplierMaterials'],
  [
    types.RECEIVE_CLASSES,
    types.RECEIVE_FAMILIES,
    types.RECEIVE_MATERIALS,
    types.RECEIVE_SUPPLIER_MATERIALS,
    types.RECEIVE_SUPPLIER_MATERIAL_FROM_GET_OR_CREATE,
  ],
);

const reducer = (state, action) => {
  switch (action.type) {
    case types.INIT_BEGIN:
      return {
        ...state,
        isFetching: true,
      };
    case types.INIT_SUCCESS:
      return {
        ...state,
        hasFetched: true,
        isFetching: false,
      };
    case types.INIT_FAILURE:
      return {
        ...state,
        isFetching: false,
      };
    case types.RECEIVE_CLASSES:
      return dot.set(state, 'classIds', action.payload.result);
    case types.RECEIVE_FAMILIES:
      return dot.set(state, 'familyIds', action.payload.result);
    case types.RECEIVE_MATERIALS:
      return dot.set(state, 'materialIds', action.payload.result);
    case types.RECEIVE_MATERIAL_SELECTOR_SETTINGS:
      return {
        ...state,
        materialSelectorSettings: action.payload.selectorSettings,
      };
    case types.RECEIVE_SUPPLIER_MATERIALS:
      return dot.set(state, 'supplierMaterialIds', action.payload.result);
    case types.RECEIVE_SUPPLIER_MATERIAL_FROM_GET_OR_CREATE:
      if (!state.supplierMaterialIds.includes(action.payload.result)) {
        // need to check for this because we have both an update and create condition
        return dot.set(
          state,
          'supplierMaterialIds',
          state.supplierMaterialIds.concat([action.payload.result]),
        );
      } else {
        return state;
      }
    case types.RECEIVE_STORED_MATERIAL_OPTIONS_FOR_CASCADER:
      return {
        ...state,
        excludedMaterialsById: action.payload.excludedMaterialsById,
        storedMaterialOptionsForCascader:
          action.payload.materialOptionsForCascader,
      };
    case types.REMOVE_SUPPLIER_MATERIAL:
      return {
        ...state,
        supplierMaterialIds: state.supplierMaterialIds.filter(
          (smid) => smid != action.payload.supplierMaterialId,
        ),
      };
    default:
      return state;
  }
};

export const getScope = (state) => state.materials;

export const getMaterialClassEntities = (state) =>
  getScope(state).entities.materialClasses;

export const getMaterialFamilyEntities = (state) =>
  getScope(state).entities.materialFamilies;

export const getMaterialEntities = (state) =>
  getScope(state).entities.materials;

export const getSupplierMaterialEntities = (state) =>
  getScope(state).entities.supplierMaterials;

export const getClasses = (state) => {
  return denormalize(
    getScope(state).classIds,
    [materialClassSchema],
    getScope(state).entities,
  );
};

export const getFamiliesByClassId = (state, classId) => {
  return denormalize(
    getScope(state).familyIds,
    [materialFamilySchema],
    getScope(state).entities,
  ).filter((family) => family.materialClassId == classId);
};

export const getFamilies = (state) => {
  return denormalize(
    getScope(state).familyIds,
    [materialFamilySchema],
    getScope(state).entities,
  );
};

export const getMaterialsByFamilyId = (state, familyId) =>
  denormalize(
    getScope(state).materialIds,
    [materialSchema],
    getScope(state).entities,
  ).filter((mat) => mat.materialFamilyId == familyId);

export const getSupplierMaterialById = (state, supplierMaterialId) =>
  denormalize(
    supplierMaterialId,
    supplierMaterialSchema,
    getScope(state).entities,
  );

export const getAllMaterials = (state) =>
  denormalize(
    getScope(state).materialIds,
    [materialSchema],
    getScope(state).entities,
  );

export const getAllSupplierMaterials = (state) =>
  denormalize(
    getScope(state).supplierMaterialIds,
    [supplierMaterialSchema],
    getScope(state).entities,
  );

export const getSupplierMaterialsByMaterialId = (state, materialId) => {
  return (getAllSupplierMaterials(state) || []).filter(
    (supplierMaterial) => supplierMaterial.materialId == materialId,
  );
};

export const getMaterialById = (state, materialId) =>
  denormalize(materialId, materialSchema, getScope(state).entities);

export const getMaterialFamilyByMaterialId = (state, materialId) =>
  denormalize(
    (getMaterialById(state, materialId) || {}).materialFamilyId,
    materialFamilySchema,
    getScope(state).entities,
  );

export const getMaterialClassByMaterialId = (state, materialId) =>
  denormalize(
    (getMaterialFamilyByMaterialId(state, materialId) || {}).materialClassId,
    materialClassSchema,
    getScope(state).entities,
  );

export const getHasFetched = (state) => getScope(state).hasFetched;

export const getIsFetching = (state) => getScope(state).isFetching;

export const getMaterialSelectorSettings = (state) =>
  getScope(state).materialSelectorSettings;

export const getStoredMaterialOptionsForCascader = (state) =>
  getScope(state).storedMaterialOptionsForCascader;

const getAllMaterialsForSupplierMaterials = (state, supplierMaterials) => {
  const materialIds = [
    ...new Set(
      supplierMaterials.map((supplierMaterial) => supplierMaterial.materialId),
    ),
  ];
  return denormalize(materialIds, [materialSchema], getScope(state).entities);
};

const getAllFamiliesForMaterials = (state, materials) => {
  const familyIds = [
    ...new Set(materials.map((material) => material.materialFamilyId)),
  ];
  return denormalize(
    familyIds,
    [materialFamilySchema],
    getScope(state).entities,
  );
};

const getAllClassesForFamilies = (state, families) => {
  const classIds = [
    ...new Set(families.map((family) => family.materialClassId)),
  ];
  return denormalize(classIds, [materialClassSchema], getScope(state).entities);
};

const constructMaterialCascaderOptions = (
  useClasses,
  useFamilies,
  useMaterials,
  useSupplierMaterials,
  useAllSupplierMaterials,
) => {
  const sortByLabel = sortBy((x) => x.label.toLowerCase());
  const toOption = (x) => ({ label: x.name, value: x.id });

  const foundMaterialIds = [];

  // first level - material classes
  const result = useClasses.map(toOption);
  for (const classOption of result) {
    const families = useFamilies.filter(
      (family) => family.materialClassId == classOption.value,
    );
    // second level - material families
    classOption.children = sortByLabel(families.map(toOption));
    for (const familyOption of classOption.children) {
      const materials = useMaterials.filter(
        (material) => material.materialFamilyId == familyOption.value,
      );
      // third level - materials
      familyOption.children = materials.map(toOption);
      // material custom names
      for (const materialOption of familyOption.children) {
        foundMaterialIds.push(materialOption.value);
        const supplierMaterials = useSupplierMaterials.filter(
          (supplierMaterial) =>
            supplierMaterial.materialId == materialOption.value,
        );
        // fourth level - optional for none, one, or multiple supplier materials
        if (supplierMaterials.length > 1) {
          const children = [];
          for (const supplierMaterial of supplierMaterials) {
            children.push({
              label: supplierMaterial.displayName,
              value: supplierMaterial.id,
              isLeaf: true, //SHOULD ALWAYS BE TRUE
            });
          }
          materialOption.children = sortByLabel(children);
        } else if (supplierMaterials.length === 1) {
          // If supplier material list is 1, check if it is a filtered list (allSupplierMaterials for materialOption > supplierMaterials for materialOption)
          // If it is filtered to 1, we need to show the 4th cascader to support the supplierMaterialId
          const allSupplierMaterials = useAllSupplierMaterials.filter(
            (allSupplierMaterial) =>
              allSupplierMaterial.materialId == materialOption.value,
          );
          if (allSupplierMaterials.length > 1) {
            materialOption.children = sortByLabel([
              {
                label: supplierMaterials[0].displayName,
                value: supplierMaterials[0].id,
                isLeaf: true, //SHOULD ALWAYS BE TRUE
              },
            ]);
          }
          // this is the default behavior of not showing the 4th cascader if there is only 1 supplier material for the material
          else {
            const firstSupplierMaterial = (supplierMaterials || [])[0];
            const { customName } = firstSupplierMaterial || {};
            materialOption.label = customName || materialOption.label;
          }
        }
      }
      familyOption.children = sortByLabel(familyOption.children);
    }
    // need to filter out families with no material children
    classOption.children = classOption.children.filter(
      (fam) => fam.children && fam.children.length,
    );
  }
  // filter out classes with no families
  // filter so additive materials are at the bottom of the cascader, otherwise alphabetical
  // TODO: remove this once existing supplier base utilizes the material selector customization
  const trueResult = result
    .filter(
      (classOption) => classOption.children && classOption.children.length,
    )
    .map((classOption) => {
      const families = classOption.children.map((family) => {
        const materials = family.children.map((material) => ({
          ...material,
          isLeaf: material.children == null,
        }));
        return {
          ...family,
          isLeaf: false,
          children: materials,
        };
      });

      return {
        ...classOption,
        children: families,
        isLeaf: false,
      };
    })
    .sort((a, b) => {
      if (a.label === 'Additive') {
        return 1;
      } else if (b.label === 'Additive') {
        return -1;
      } else {
        return a.label - b.label;
      }
    });
  return { cascaderOptions: trueResult, foundMaterialIds };
};

export const getMaterialOptionsForCascaderForSupplierMaterials = (
  state,
  supplierMaterials,
) => {
  const allSupplierMaterials = getAllSupplierMaterials(state);
  const useMaterials = getAllMaterialsForSupplierMaterials(
    state,
    supplierMaterials,
  );
  const useFamilies = getAllFamiliesForMaterials(state, useMaterials);
  const useClasses = getAllClassesForFamilies(state, useFamilies);
  const { cascaderOptions } = constructMaterialCascaderOptions(
    useClasses,
    useFamilies,
    useMaterials,
    supplierMaterials,
    allSupplierMaterials,
  );
  return cascaderOptions;
};

/**
 * Create 3-level tree (material classes -> material families -> materials)
 * compatible with antd's Cascader component
 * Also track what supplier materials are excluded
 */
export const getMaterialOptionsForCascader = (state) => {
  const selectorSettings = getMaterialSelectorSettings(state);
  const {
    excludedMaterialClassIds,
    excludedMaterialFamilyIds,
    excludedMaterialIds,
    onlyIncludeSupplierMaterials,
  } = selectorSettings;
  const allSupplierMaterials = getAllSupplierMaterials(state);

  if (onlyIncludeSupplierMaterials) {
    const cascaderOptions = getMaterialOptionsForCascaderForSupplierMaterials(
      state,
      allSupplierMaterials,
    );
    return {
      cascaderOptions,
      excludedMaterialsById: {},
    };
  } else {
    const useClasses = denormalize(
      getScope(state).classIds.filter(
        (id) => !excludedMaterialClassIds.includes(id),
      ),
      [materialClassSchema],
      getScope(state).entities,
    );
    const useClassIds = useClasses.map((matClass) => matClass.id);
    const useFamilies = denormalize(
      getScope(state).familyIds.filter(
        (id) => !excludedMaterialFamilyIds.includes(id),
      ),
      [materialFamilySchema],
      getScope(state).entities,
    ).filter((family) => useClassIds.includes(family.materialClassId));
    const useFamilyIds = useFamilies.map((family) => family.id);
    const allMaterials = denormalize(
      getScope(state).materialIds.filter(
        (id) => !excludedMaterialIds.includes(id),
      ),
      [materialSchema],
      getScope(state).entities,
    );
    const useMaterials = allMaterials.filter((material) =>
      useFamilyIds.includes(material.materialFamilyId),
    );

    const { cascaderOptions, foundMaterialIds } =
      constructMaterialCascaderOptions(
        useClasses,
        useFamilies,
        useMaterials,
        allSupplierMaterials,
        allSupplierMaterials,
      );
    const excludedMaterialsById = {};
    for (const materialId of Object.keys(getMaterialEntities(state))) {
      if (foundMaterialIds.includes(parseInt(materialId))) {
        excludedMaterialsById[parseInt(materialId)] = false;
      } else {
        excludedMaterialsById[parseInt(materialId)] = true;
      }
    }

    return { cascaderOptions, excludedMaterialsById };
  }
};

export const getIsMaterialExcluded = (state, materialId) =>
  getScope(state).excludedMaterialsById[materialId];

export default reduceReducers(entitiesReducer, reducer, initialState);
